import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ShareGrid } from '../lib/types';
import { ORIGIN, pathFor, type Lang } from '../lib/routes';
import { shareText, socialLinks, copyText } from '../lib/share';
import { renderShareCanvas, canvasToBlob } from '../lib/shareImage';
import { BRAND_ICON } from '../lib/brandIcons';
import { useApp } from '../app-context';

interface Props {
  mode: string;
  lang: Lang;
  grid: ShareGrid;
  attempts: number;
  max: number | null;
  won: boolean;
  streak: number;
  day: number;
  onClose: () => void;
}

export default function ShareModal({ mode, lang, grid, attempts, max, won, streak, day, onClose }: Props) {
  const { t } = useApp();
  const [imgUrl, setImgUrl] = useState<string>('');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [toast, setToast] = useState('');

  const text = shareText(mode, grid, won, streak, attempts, max);
  const url = ORIGIN + pathFor(lang, mode);
  const fullText = `${text}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const canvas = await renderShareCanvas({ mode, grid, attempts, max, won, streak, day });
      if (cancelled) return;
      setImgUrl(canvas.toDataURL('image/png'));
      setBlob(await canvasToBlob(canvas));
    })();
    return () => { cancelled = true; };
  }, [mode, grid, attempts, max, won, streak, day]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(''), 1600);
  }

  async function onShareImage() {
    if (blob && navigator.canShare?.({ files: [new File([blob], 'animedle.png', { type: 'image/png' })] })) {
      try {
        await navigator.share({ files: [new File([blob], 'animedle.png', { type: 'image/png' })], text: fullText });
        return;
      } catch { /* fall through */ }
    }
    if (imgUrl) {
      const a = document.createElement('a');
      a.href = imgUrl;
      a.download = `animedle-${mode}-${day}.png`;
      a.click();
      flash(t('toast.copied'));
    }
  }

  const socials = socialLinks(`${fullText}\n${url}`, url);

  return createPortal(
    <div className="scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="x" onClick={onClose} aria-label="Close">×</button>
        <h2 className="modal-h">{t('share')}</h2>

        {imgUrl ? <img className="sharepreview" src={imgUrl} alt="Share card" /> : <div className="sharepreview ph" />}

        <pre className="resulttext">{fullText}</pre>

        <div className="sharerow">
          <button className="sbtn primary" onClick={onShareImage}>⤴ {t('share.image')}</button>
          <button className="sbtn" onClick={async () => { await copyText(fullText); flash(t('toast.copied')); }}>{t('share.copyResult')}</button>
          <button className="sbtn" onClick={async () => { await copyText(url); flash(t('toast.copied')); }}>{t('share.copyLink')}</button>
        </div>

        <div className="socials">
          {socials.map((s) => (
            <a key={s.key} className="social" href={s.href} target="_blank" rel="noopener noreferrer" style={{ background: s.color }} aria-label={s.label} title={s.label}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#fff"><path d={BRAND_ICON[s.key]} /></svg>
            </a>
          ))}
        </div>

        {toast && <div className="toast in-modal">{toast}</div>}
      </div>
    </div>,
    document.body,
  );
}
